<template>
  <ion-app>
    <ion-router-outlet />
  </ion-app>
</template>

<script setup lang="ts">
import { IonApp, IonRouterOutlet, useBackButton, useIonRouter, toastController } from '@ionic/vue'
import { onMounted, onErrorCaptured } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useCapacitorStore } from '@/stores/capacitor'
import { useThemeStore } from '@/stores/theme'
/* MATT-GEOLOCATION-DISABLED: import { useTrackingStore } from '@/stores/tracking' */
import { PushNotifications } from '@capacitor/push-notifications'
import { logger } from '@/services/logger'
import { StatusBar } from '@capacitor/status-bar'
import { App } from '@capacitor/app'

const router = useRouter()
const ionRouter = useIonRouter()
const route = useRoute()
const capacitorStore = useCapacitorStore()
const themeStore = useThemeStore()

// Centralized back button handler (Android hardware/gesture back).
// iOS swipe-back is handled by ion-router-outlet's gesture system,
// which correctly uses the Ionic view stack and doesn't trigger when
// there is no previous view (e.g. direct/shared links).
const fallbackRoutes: Record<string, string> = {
  '/tabs/profile/edit': '/tabs/profile',
  '/tabs/poi/create': '/tabs/explore',
}

function getBackFallback(path: string): string | null {
  if (fallbackRoutes[path]) return fallbackRoutes[path]
  if (/^\/tabs\/poi\/\d+\/edit$/.test(path)) return path.replace('/edit', '')
  if (/^\/tabs\/poi\/\d+$/.test(path)) return '/tabs/explore'
  return null // tab pages — exit app
}

useBackButton(10, () => {
  // Prefer router.back() when history exists — it correctly reveals the already-mounted
  // TabsLayout behind the current view without triggering a second tabs navigation
  // (which causes both tabs to flash active simultaneously).
  // ionRouter.navigate is only used when there is no history (e.g. deep-linked directly
  // to /profile/edit) so the back animation still plays.
  if (window.history.state?.back) {
    router.back()
    return
  }
  const fallback = getBackFallback(route.path)
  if (fallback) {
    ionRouter.navigate(fallback, 'back', 'replace')
    return
  }
  App.exitApp()
})
/* MATT-GEOLOCATION-DISABLED: const trackingStore = useTrackingStore() */

// Global error boundary — intercepts component render/lifecycle errors before they blank the app
onErrorCaptured((err) => {
  logger.apiDebug('Global error captured', { error: String(err) })
  toastController.create({
    message: 'Ocurrió un error inesperado',
    duration: 3000,
    color: 'danger',
    position: 'bottom'
  }).then(toast => toast.present())
  return false // prevent propagation (keeps app alive)
})

// Helper function to request basic permissions on startup
async function requestBasicPermissions() {
  logger.apiDebug('Starting basic permission requests')

  try {
    // Request location permissions first
    const locationGranted = await capacitorStore.requestLocationPermissions()
    logger.apiDebug('Location permission result', { granted: locationGranted })

    // Request notification permissions
    try {
      const notificationResult = await PushNotifications.requestPermissions()
      logger.apiDebug('Notification permission result', notificationResult)
    } catch (notificationError: any) {
      logger.apiDebug('Notification permission failed', { error: notificationError?.message })
      // Continue even if notifications fail
    }

    return locationGranted
  } catch (error: any) {
    logger.apiDebug('Permission request failed', { error: error?.message })
    return false
  }
}

onMounted(async () => {
  logger.apiDebug('App mounted, starting initialization')

  // Initialize theme first
  themeStore.initializeTheme()

  // Initialize Capacitor plugins
  await capacitorStore.initializePlugins()

  // Ensure content does not render under the status bar on native
  if (capacitorStore.isNative) {
    try {
      await StatusBar.setOverlaysWebView({ overlay: false })
    } catch (e) {
      // Non-fatal if status bar settings are unavailable
    }
  }

  /* MATT-GEOLOCATION-DISABLED: Location permission prompts and initialization
  // Check if we should prompt for permissions
  const hasPromptedForPermissions = await storageService.retrieve('permissions_prompted')

  if (!hasPromptedForPermissions && capacitorStore.isNative) {
    // First time user on native - prompt for permissions
    logger.apiDebug('First time native user, prompting for permissions')
    const result = await alertController.create({
      header: t('app.permissions'),
      message: t('app.permissionsMessage'),
      buttons: [
        {
          text: t('app.notNow'),
          role: 'cancel',
          handler: async () => {
            await storageService.store('permissions_prompted', true)
          }
        },
        {
          text: t('app.enablePermissions'),
          handler: async () => {
            await storageService.store('permissions_prompted', true)
            const locationGranted = await requestBasicPermissions()
            if (locationGranted) {
              await trackingStore.initializeLocation()
            }
          }
        }
      ]
    })
    await result.present()
  } else {
    // Not first time or web platform - try to initialize location
    logger.apiDebug('Initializing location (platform-appropriate)')
    await trackingStore.initializeLocation()
  }
  */
})
</script>

<style>
/* Global app styles will be imported from theme files */
</style>
